import { Avatar, Segmented, Space, Table, Tag, Typography } from 'antd';
import React, { useState } from 'react';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../common/PageTitle';
import { useGetUsersListQuery } from '../../../features/courses/coursesApi';

const { Text } = Typography;

const roleColors = {
    admin: 'volcano',
    teacher: 'geekblue',
    student: 'green',
};

const ROLE_FILTERS = ['all', 'admin', 'teacher', 'student'];

const formatDate = (date) => {
    if (!date) {
        return '-';
    }
    return new Intl.DateTimeFormat('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(new Date(date));
};

const AdminUsersPage = () => {
    const { t } = useTranslation();
    const [role, setRole] = useState('all');
    // 'all' maps to no ?role= filter; the backend returns every user.
    const { data: users, isLoading } = useGetUsersListQuery(
        role === 'all' ? '' : role
    );

    const columns = [
        {
            key: 'user',
            title: t('admin.users.list.user'),
            render: (data) => {
                const fullName = `${data.first_name || ''} ${
                    data.last_name || ''
                }`.trim();
                return (
                    <Space direction="horizontal">
                        <Avatar
                            size={'large'}
                            icon={<UserOutlined />}
                            src={data?.profile_picture}
                        />
                        <Space direction="vertical" size={0}>
                            <Text strong>{fullName || data.username}</Text>
                            <Text type="secondary">{data.email}</Text>
                        </Space>
                    </Space>
                );
            },
        },
        {
            key: 'roles',
            title: t('admin.users.list.roles'),
            render: (data) => {
                const roles = data?.roles || [];
                if (!roles.length) {
                    return <Text type="secondary">-</Text>;
                }
                return (
                    <Space size={[0, 4]} wrap>
                        {roles.map((r) => (
                            <Tag key={r} color={roleColors[r] || 'default'}>
                                {t(`admin.users.roles.${r}`, r)}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            key: 'study_group',
            title: t('admin.users.list.studyGroup'),
            render: (data) =>
                data?.study_group ? (
                    <Text>{data.study_group}</Text>
                ) : (
                    <Text type="secondary">-</Text>
                ),
        },
        {
            key: 'date_joined',
            title: t('admin.users.list.joined'),
            render: (data) => formatDate(data?.date_joined),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <PageTitle title={t('admin.users.header') as String} />
            <Segmented
                value={role}
                onChange={(value) => setRole(value as string)}
                options={ROLE_FILTERS.map((value) => ({
                    label: t(`admin.users.filter.${value}`),
                    value,
                }))}
            />
            <Table
                columns={columns}
                dataSource={users}
                loading={isLoading}
                locale={{ emptyText: t('admin.users.list.empty') }}
                rowKey={'uuid'}
                pagination={{ pageSize: 20 }}
            />
        </Space>
    );
};

export default AdminUsersPage;
